import { CityPageQueryQuery } from '@medprev-web-app/api-graphql-client';

import { NotifyMeButton } from '@/app/(main)/(pages)/_components/search-section/buttons/notify-me-button.component';
import { TelemedicineScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/telemedicine-schedule-button.component';
import { AssistedPurchaseScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/assisted-purchase-schedule-button.component';
import { BranchScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/branch-schedule-button.component';
import { MedprevOnlineScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/medprev-online-schedule-button.component';
import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { ButtonColor, ButtonVariant } from '@/components/ui/button/button.types';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

interface SearchSectionCtaButtonsProps {
  cityConfig: CityPageQueryQuery;
  firstButtonColor?: ButtonColor;
  secondButtonVariant?: ButtonVariant;
  isAfterBranchesSection?: boolean;
  from?: string;
  sessionName?: string;
  medprevScheduleButtonAnalyticsEventName?: AnalyticsEnumKeyType;
  assistedPurchaseButtonAnalyticsEventName?: AnalyticsEnumKeyType;
  telemedicineButtonAnalyticsEventName?: AnalyticsEnumKeyType;
}

export const SearchSectionCtaButtons = ({
  cityConfig,
  firstButtonColor,
  secondButtonVariant,
  isAfterBranchesSection = false,
  from,
  sessionName,
  medprevScheduleButtonAnalyticsEventName,
  assistedPurchaseButtonAnalyticsEventName,
  telemedicineButtonAnalyticsEventName,
}: SearchSectionCtaButtonsProps) => {
  const {
    branches,
    branchesLength,
    hasTelemedicine,
    hasAssistedPurchase,
    hasMedprevOnline,
    hasBranches,
    noServiceType,
  } = getServiceChannelFlags(cityConfig);

  const whatsapp = branches[0]?.whatsapp;
  const primaryColor = firstButtonColor ?? 'success';
  const secondaryVariant = secondButtonVariant ?? 'secondary';

  const buttons: React.ReactNode[] = [];

  if (hasMedprevOnline) {
    buttons.push(
      <MedprevOnlineScheduleButton
        key="online"
        color={primaryColor}
        from={from}
        sessionName={sessionName}
        data-analytics-event={medprevScheduleButtonAnalyticsEventName}
      />,
    );
  }

  if (hasAssistedPurchase) {
    const variant = hasMedprevOnline ? secondaryVariant : 'default';
    const color = hasMedprevOnline ? undefined : primaryColor;

    buttons.push(
      <AssistedPurchaseScheduleButton
        key="assisted"
        variant={variant}
        color={color}
        from={from}
        sessionName={sessionName}
        data-analytics-event={assistedPurchaseButtonAnalyticsEventName}
      />,
    );
  }

  if (hasBranches) {
    const onlyBranches = !hasMedprevOnline && !hasAssistedPurchase;
    const combinedWithOthers
      = (hasMedprevOnline && !hasAssistedPurchase)
        || (!hasMedprevOnline && hasAssistedPurchase);

    if (onlyBranches) {
      buttons.push(
        <BranchScheduleButton
          key="branch"
          branchesLength={branchesLength}
          whatsapp={whatsapp!}
          isAfterBranchesSection={isAfterBranchesSection}
          variant="default"
          color={primaryColor}
        />,
      );
    } else if (combinedWithOthers) {
      buttons.push(
        <BranchScheduleButton
          key="branch"
          branchesLength={branchesLength}
          whatsapp={whatsapp!}
          isAfterBranchesSection={isAfterBranchesSection}
          variant={secondaryVariant}
        />,
      );
    }
  }

  if (hasTelemedicine && !hasMedprevOnline) {
    const onlyTelemedicine = !hasBranches && !hasAssistedPurchase;
    const combinedWithOthers
      = (hasBranches && !hasAssistedPurchase)
        || (!hasBranches && hasAssistedPurchase);

    if (onlyTelemedicine) {
      buttons.push(
        <TelemedicineScheduleButton
          key="telemedicine"
          color={primaryColor}
          from={from}
          sessionName={sessionName}
          data-analytics-event={telemedicineButtonAnalyticsEventName}
        />,
      );
    } else if (combinedWithOthers) {
      buttons.push(
        <TelemedicineScheduleButton
          key="telemedicine"
          variant={secondaryVariant}
          color="primary"
          from={from}
          sessionName={sessionName}
          data-analytics-event={telemedicineButtonAnalyticsEventName}
        />,
      );
    }
  }

  if (noServiceType) {
    buttons.push(<NotifyMeButton key="notify" color={primaryColor} />);
  }

  return <>{buttons}</>;
};
